import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { FiCopy, FiLock, FiMove, FiTrash2, FiType, FiUnlock, FiX } from "react-icons/fi";
import {
  AccessLevel,
  BulkDuplicateMediaDocument,
  type BulkDuplicateMediaMutation,
  type BulkDuplicateMediaMutationVariables,
  UpdateMediaAccessLevelDocument,
  type UpdateMediaAccessLevelMutation,
  type UpdateMediaAccessLevelMutationVariables,
} from "../../../generated/graphql";
import type { MediaItem } from "../types";

interface FloatingActionBarProps {
  selectedItems: MediaItem[];
  selectedCount: number;
  onClearSelection: () => void;
  onRename: (item: MediaItem) => void;
  onMove: (items: MediaItem[]) => void;
  onDelete: (items: MediaItem[]) => void;
}

export function FloatingActionBar({
  selectedItems,
  selectedCount,
  onClearSelection,
  onRename,
  onMove,
  onDelete,
}: FloatingActionBarProps) {
  const [duplicating, setDuplicating] = useState(false);
  const [updatingAccess, setUpdatingAccess] = useState(false);

  const [bulkDuplicateMedia] = useMutation<
    BulkDuplicateMediaMutation,
    BulkDuplicateMediaMutationVariables
  >(BulkDuplicateMediaDocument, {
    refetchQueries: ["GetMedia"],
    onCompleted: (data) => {
      const count = data.bulkDuplicateMedia.length;
      toast.success(`Duplicated ${count} ${count === 1 ? "item" : "items"}`);
      onClearSelection();
    },
    onError: (error) => {
      toast.error(`Failed to duplicate: ${error.message}`);
    },
  });

  const [updateAccessLevel] = useMutation<
    UpdateMediaAccessLevelMutation,
    UpdateMediaAccessLevelMutationVariables
  >(UpdateMediaAccessLevelDocument);

  const isSingleSelection = selectedCount === 1;
  const firstItem = selectedItems[0] || null;
  const allPrivate = selectedItems.every((item) => item.accessLevel === AccessLevel.Private);

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const ids = selectedItems.map((item) => item.id);
      await bulkDuplicateMedia({ variables: { ids } });
    } finally {
      setDuplicating(false);
    }
  };

  const handleToggleAccessLevel = async () => {
    setUpdatingAccess(true);
    try {
      const newAccessLevel = allPrivate ? AccessLevel.Public : AccessLevel.Private;
      for (const item of selectedItems) {
        await updateAccessLevel({
          variables: {
            id: item.id,
            accessLevel: newAccessLevel,
          },
        });
      }
      onClearSelection();
    } finally {
      setUpdatingAccess(false);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 transform">
      <div className="flex items-center gap-3 rounded-lg border bg-white px-6 py-3 shadow-lg">
        {/* Selection count */}
        <div className="flex items-center gap-2 border-r pr-4">
          <span className="text-sm font-medium text-gray-900">
            {selectedCount} {selectedCount === 1 ? "item" : "items"} selected
          </span>
          <button
            type="button"
            onClick={onClearSelection}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Clear selection"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Rename - single only */}
          {isSingleSelection && firstItem && (
            <button
              type="button"
              onClick={() => onRename(firstItem)}
              className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
              title="Rename"
            >
              <FiType className="h-4 w-4" />
              <span className="hidden sm:inline">Rename</span>
            </button>
          )}

          {/* Move */}
          <button
            type="button"
            onClick={() => onMove(selectedItems)}
            className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
            title="Move"
          >
            <FiMove className="h-4 w-4" />
            <span className="hidden sm:inline">Move</span>
          </button>

          {/* Duplicate */}
          <button
            type="button"
            onClick={handleDuplicate}
            disabled={duplicating}
            className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            title="Duplicate"
          >
            <FiCopy className="h-4 w-4" />
            <span className="hidden sm:inline">{duplicating ? "Duplicating..." : "Duplicate"}</span>
          </button>

          {/* Toggle visibility */}
          <button
            type="button"
            onClick={handleToggleAccessLevel}
            disabled={updatingAccess}
            className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            title={allPrivate ? "Make public" : "Make private"}
          >
            {allPrivate ? (
              <>
                <FiUnlock className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {updatingAccess ? "Updating..." : "Make Public"}
                </span>
              </>
            ) : (
              <>
                <FiLock className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {updatingAccess ? "Updating..." : "Make Private"}
                </span>
              </>
            )}
          </button>

          {/* Delete */}
          <button
            type="button"
            onClick={() => onDelete(selectedItems)}
            className="flex items-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
            title="Delete"
          >
            <FiTrash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Delete</span>
          </button>
        </div>
      </div>
    </div>
  );
}
